import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Copy, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";

type Kind = "idea" | "project";

interface Props {
  kind: Kind;
  entityId: string;
  initialEnabled: boolean;
  initialSlug: string | null;
}

const ShareDashboardCard = ({ kind, entityId, initialEnabled, initialSlug }: Props) => {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(initialEnabled);
  const [slug, setSlug] = useState<string | null>(initialSlug);

  const rpcName = kind === "idea" ? "set_idea_share" : "set_project_share";
  const paramKey = kind === "idea" ? "_idea_id" : "_project_id";
  const shareBase = kind === "idea" ? "/share/i" : "/share/p";

  const toggle = useMutation({
    mutationFn: async (enable: boolean) => {
      const { data, error } = await supabase.rpc(rpcName as any, {
        [paramKey]: entityId,
        _enable: enable,
      } as any);
      if (error) throw error;
      return { enable, slug: (data as string | null) ?? null };
    },
    onSuccess: ({ enable, slug: nextSlug }) => {
      setEnabled(enable);
      if (nextSlug) setSlug(nextSlug);
      toast.success(enable ? "Share link is live" : "Share link disabled");
      qc.invalidateQueries({ queryKey: [kind, entityId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not update share link"),
  });

  const url = slug ? `${window.location.origin}${shareBase}/${slug}` : "";

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start gap-3 mb-3">
        <Share2 className="h-4 w-4 text-primary mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-base font-semibold">Stakeholder dashboard</h3>
            <Switch
              checked={enabled}
              disabled={toggle.isPending}
              onCheckedChange={(v) => toggle.mutate(v)}
              aria-label="Enable public share link"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            A read-only page you can share with a funder or partner. Shows the {kind}'s
            summary, updates, help asks{kind === "project" ? ", score band," : ""} and revision history.
          </p>
        </div>
      </div>

      {enabled && slug && (
        <div className="flex items-center gap-2 mt-3">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 rounded-md border border-border bg-background px-3 py-2 text-xs font-mono truncate"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(url);
                toast.success("Link copied");
              } catch {
                toast.error("Copy failed");
              }
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={url} target="_blank" rel="noreferrer" aria-label="Open share link">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ShareDashboardCard;